import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message as toast,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import UserPicker from 'pages/star-shared/components/user-picker';
import { durationText } from 'pages/support-shared/utils';
import { useFeed } from './shared/context';
import { PersonAvatar, Pill, ReasonTags } from './shared/components';
import {
  REPORT_TARGET_STATUS_LABEL,
  RESOLUTION_LABEL,
  RESTRICT_SCOPE_LABEL,
  TARGET_TYPE_STYLE,
} from './shared/labels';
import {
  FeedBlockedRow,
  FeedReportGroupRow,
  FeedReportItem,
  FeedReportSnapshot,
  FeedRestrictionRow,
  PersonRef,
} from './shared/types';
import {
  feedError,
  fmtDateTime,
  personText,
  rankOf,
  useFeedList,
  useUrlFilters,
} from './shared/utils';
import ResolveModal from './resolve-modal';
import RestrictModal from './restrict-modal';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;

const PersonCell = ({ person }: { person?: PersonRef | null }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
    <PersonAvatar name={person?.name} avatar={person?.avatar} size={32} />
    <div style={{ minWidth: 0 }}>
      <Typography.Text ellipsis style={{ fontWeight: 600, display: 'block' }}>
        {personText(person)}
      </Typography.Text>
      {person?.classLabel && (
        <span style={{ fontSize: 12, color: colors.textSecondary }}>
          {person.classLabel === 'NPC' ? 'Nhân viên' : person.classLabel}
        </span>
      )}
    </div>
  </div>
);

/** Pill thời gian chờ: < 12 giờ xanh, 12–24 giờ cam, > 24 giờ đỏ "Quá hạn · 26 giờ". */
const WaitPill = ({ firstAt }: { firstAt: string }) => {
  const hours = (Date.now() - new Date(firstAt).getTime()) / 3600000;
  const text = durationText(firstAt);
  if (hours >= 24) {
    return (
      <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
        Quá hạn · {text}
      </Pill>
    );
  }
  if (hours >= 12) {
    return (
      <Pill color={colors.warningInk} bg={colors.warningSoft}>
        {text}
      </Pill>
    );
  }
  return (
    <Pill color={colors.infoInk} bg={colors.infoSoft}>
      {text}
    </Pill>
  );
};

/** Danh sách từng người báo cáo của một nội dung (D15). */
const ReportItems = ({ group }: { group: FeedReportGroupRow }) => {
  const { reportReasonLabel } = useFeed();
  const [items, setItems] = useState<FeedReportItem[] | null>(null);
  const [snapshot, setSnapshot] = useState<FeedReportSnapshot | null>(null);
  useEffect(() => {
    let alive = true;
    AxiosUtils.get(API.feedAdminReportItems, { targetType: group.targetType, targetId: group.targetId })
      .then((res: any) => {
        const list = res?.data?.data ?? res?.data ?? [];
        if (alive) setItems(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (alive) setItems([]);
        toast.error(feedError(err));
      });
    return () => {
      alive = false;
    };
  }, [group.targetType, group.targetId]);
  if (!items) return <Spin size="small" />;
  return (
    <>
      <List
        size="small"
        dataSource={items}
        locale={{ emptyText: 'Không có dữ liệu.' }}
        renderItem={(r) => (
          <List.Item
            key={r.id}
            actions={
              r.snapshot
                ? [
                    <Button key="snap" size="small" onClick={() => setSnapshot(r.snapshot)}>
                      Nội dung lúc bị báo cáo
                    </Button>,
                  ]
                : undefined
            }
          >
            <Space size={8} wrap>
              <strong>{personText(r.reporter)}</strong>
              <Pill color={colors.textPrimary} bg={colors.surfaceMuted}>
                {reportReasonLabel(r.reason)}
              </Pill>
              {r.note && <span>“{r.note}”</span>}
              <span style={{ fontSize: 12, color: colors.textSecondary }}>{fmtDateTime(r.createdAt)}</span>
            </Space>
          </List.Item>
        )}
      />
      <Modal
        open={!!snapshot}
        title="Nội dung lúc bị báo cáo"
        footer={null}
        onCancel={() => setSnapshot(null)}
        width={560}
        destroyOnHidden
      >
        {snapshot && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {snapshot.authorName && (
              <div className={styles.hint}>
                {snapshot.authorName}
                {snapshot.createdAt ? ` · ${fmtDateTime(snapshot.createdAt)}` : ''}
              </div>
            )}
            <div className={styles.content}>
              {snapshot.content || <span style={{ color: colors.textMuted }}>(không có chữ)</span>}
            </div>
            {(snapshot.mediaIds?.length || snapshot.mediaKeys?.length) && (
              <div className={styles.hint}>
                Có {snapshot.mediaIds?.length || snapshot.mediaKeys?.length} ảnh/video — xem trong ngăn chi tiết bài
                (file được giữ làm bằng chứng).
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

// ------------------------------------------------------------------ hàng chờ (D14)

const QueueView = ({ status }: { status: 'OPEN' | 'RESOLVED' | 'DISMISSED' }) => {
  const { refreshKey, summary, openPost, goTab, reportReasonLabel, reportReasons } = useFeed();
  const { get, set } = useUrlFilters();
  const page = Number(get('page')) || 1;
  const [resolving, setResolving] = useState<FeedReportGroupRow | null>(null);
  const { items, total, loading } = useFeedList<FeedReportGroupRow>(
    API.feedAdminReports,
    { status, targetType: get('targetType'), reason: get('reason'), page, limit: PAGE_SIZE },
    { refreshKey }
  );

  const openTarget = (g: FeedReportGroupRow) => {
    if (g.targetType === 'POST') openPost(g.postId || g.targetId);
    else if (g.targetType === 'COMMENT' && g.postId) openPost(g.postId, 'comments');
    else if (g.targetType === 'USER' && g.targetUser) {
      goTab('posts', { authorId: String(g.targetUser.userId), authorName: g.targetUser.name });
    }
  };

  const columns: ColumnsType<FeedReportGroupRow> = [
    {
      title: 'Nội dung bị báo cáo',
      key: 'target',
      render: (_, g) => {
        const s = TARGET_TYPE_STYLE[g.targetType];
        return (
          <div style={{ minWidth: 280, cursor: 'pointer' }} onClick={() => openTarget(g)}>
            <Space size={6} style={{ marginBottom: 2 }}>
              {s && (
                <Pill color={s.color} bg={s.bg}>
                  {s.label}
                </Pill>
              )}
              {g.targetType !== 'USER' && <span className={styles.muted}>#{g.targetId}</span>}
            </Space>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 2 }}>
              <Typography.Link>{g.preview || '(không có chữ)'}</Typography.Link>
            </Typography.Paragraph>
            <div className={styles.muted}>
              {g.targetUser ? `của ${personText(g.targetUser)}` : ''}
              {g.targetStatus ? ` · ${REPORT_TARGET_STATUS_LABEL[g.targetStatus] || g.targetStatus}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Người báo cáo',
      key: 'reporters',
      width: 148,
      render: (_, g) => (
        <div style={{ lineHeight: '20px' }}>
          <div style={{ fontWeight: 700 }}>{g.reporterCount} người</div>
          <div className={styles.muted}>{g.reportCount} lượt</div>
        </div>
      ),
    },
    {
      title: 'Lý do',
      key: 'reasons',
      width: 200,
      render: (_, g) => <ReasonTags reasons={g.reasons || []} labelOf={reportReasonLabel} />,
    },
    {
      title: status === 'OPEN' ? 'Chờ' : 'Xử lý',
      key: 'wait',
      width: 156,
      render: (_, g) =>
        status === 'OPEN' ? (
          <WaitPill firstAt={g.firstAt} />
        ) : (
          <div style={{ fontSize: 13, lineHeight: '20px' }}>
            <div style={{ fontWeight: 600 }}>{g.resolution ? RESOLUTION_LABEL[g.resolution] || g.resolution : '—'}</div>
            <div className={styles.muted}>
              {g.resolvedBy ? personText(g.resolvedBy) : ''}
              {g.resolvedAt ? ` · ${fmtDateTime(g.resolvedAt)}` : ''}
            </div>
            {g.reporterReply && (
              <div
                className={styles.muted}
                title={g.reporterReply}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                Đã phản hồi: {g.reporterReply}
              </div>
            )}
          </div>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, g) =>
        status === 'OPEN' ? (
          <Button type="primary" size="small" onClick={() => setResolving(g)}>
            Xử lý
          </Button>
        ) : null,
    },
  ];

  const emptyText =
    status === 'OPEN'
      ? 'Không có báo cáo nào đang chờ. Tuyệt vời!'
      : status === 'RESOLVED'
      ? 'Chưa có báo cáo nào được xử lý.'
      : 'Chưa có báo cáo nào bị bỏ qua.';

  return (
    <div>
      <div className={styles.filters}>
        <Select
          allowClear
          placeholder="Loại nội dung: Tất cả"
          style={{ width: 200 }}
          value={get('targetType')}
          options={[
            { value: 'POST', label: 'Bài viết' },
            { value: 'COMMENT', label: 'Bình luận' },
            { value: 'USER', label: 'Tài khoản' },
          ]}
          onChange={(v) => set({ targetType: v })}
        />
        <Select
          allowClear
          placeholder="Lý do: Tất cả"
          style={{ width: 240 }}
          value={get('reason')}
          options={reportReasons}
          onChange={(v) => set({ reason: v })}
        />
      </div>
      {status === 'OPEN' && summary && (
        <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className={styles.hint}>
            {summary.openReportTargets} nội dung · {summary.openReports} lượt báo cáo
            {summary.oldestOpenReportAt ? ` · cũ nhất ${durationText(summary.oldestOpenReportAt)} trước` : ''}
          </div>
          {summary.overdueReports > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${summary.overdueReports} nội dung chờ quá 24 giờ`}
              description="Nghị định 147/2024 yêu cầu gỡ nội dung vi phạm trong 24 giờ kể từ khi biết. Xử lý các dòng tô đỏ trước."
            />
          )}
        </div>
      )}
      <div className="custom-table">
        <Table<FeedReportGroupRow>
          rowKey={(g) => `${g.targetType}-${g.targetId}`}
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText }}
          scroll={items.length ? { x: 904 } : undefined}
          rowClassName={(g) => (status === 'OPEN' && g.overdue ? styles.overdueRow : '')}
          expandable={{ expandedRowRender: (g) => <ReportItems group={g} /> }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
      {resolving && (
        <ResolveModal
          group={resolving}
          onClose={() => setResolving(null)}
          onViewFull={(g) => {
            setResolving(null);
            openTarget(g);
          }}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------------ người bị hạn chế (D17–D19)

const RestrictionsView = () => {
  const { refreshKey, myRank, afterAction } = useFeed();
  const { get, set } = useUrlFilters();
  const page = Number(get('page')) || 1;
  const showAll = get('active') === 'all';
  const userId = get('userId');
  const [restrictOpen, setRestrictOpen] = useState(false);
  const { items, total, loading } = useFeedList<FeedRestrictionRow>(
    API.feedAdminRestrictions,
    { userId, active: showAll ? undefined : true, page, limit: PAGE_SIZE },
    { refreshKey }
  );

  const revoke = async (row: FeedRestrictionRow) => {
    try {
      await AxiosUtils.post(`${API.feedAdminRestrictions}/${row.id}/revoke`, {});
      toast.success(`Đã gỡ hạn chế cho ${personText(row.user)}.`);
      afterAction();
    } catch (err) {
      toast.error(feedError(err));
    }
  };

  const columns: ColumnsType<FeedRestrictionRow> = [
    { title: 'Người', key: 'user', width: 192, render: (_, r) => <PersonCell person={r.user} /> },
    {
      title: 'Không được',
      key: 'scope',
      width: 176,
      render: (_, r) => RESTRICT_SCOPE_LABEL[r.scope] || r.scope,
    },
    {
      title: 'Đến',
      key: 'endsAt',
      width: 132,
      render: (_, r) => (
        <div style={{ color: r.active ? undefined : colors.textMuted, fontSize: 13 }}>
          {r.endsAt ? fmtDateTime(r.endsAt) : 'Vô thời hạn'}
          {r.revokedAt && (
            <div style={{ fontSize: 12 }}>đã gỡ bởi {personText(r.revokedBy)}</div>
          )}
          {!r.revokedAt && !r.active && <div style={{ fontSize: 12 }}>đã hết hạn</div>}
        </div>
      ),
    },
    {
      title: 'Lý do',
      key: 'reason',
      render: (_, r) => (
        <Typography.Paragraph ellipsis={{ rows: 2, tooltip: r.reason }} style={{ marginBottom: 0, minWidth: 220 }}>
          {r.reason}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'Người đặt',
      key: 'createdBy',
      width: 144,
      render: (_, r) => (
        <div style={{ fontSize: 13 }}>
          {personText(r.createdBy)}
          <div className={styles.muted}>{fmtDateTime(r.createdAt)}</div>
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (_, r) => {
        if (!r.active) return null;
        const higher = rankOf(r.createdByTier) > myRank;
        return (
          <Popconfirm
            title={`Gỡ hạn chế cho ${personText(r.user)}?`}
            okText="Gỡ"
            cancelText="Huỷ"
            disabled={higher}
            onConfirm={() => revoke(r)}
          >
            <Button
              size="small"
              disabled={higher}
              title={higher ? 'Hạn chế do người bậc cao hơn đặt — chỉ người cùng bậc trở lên gỡ được.' : undefined}
            >
              Gỡ
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.filters} style={{ marginBottom: 0 }}>
          <div style={{ width: 260 }}>
            <UserPicker
              size="middle"
              placeholder="Lọc theo người"
              value={userId ? { value: Number(userId), label: get('userName') || `#${userId}` } : undefined}
              onChange={(v) =>
                set({ userId: v?.value, userName: v?.label ? String(v.label).split(' — ')[0] : undefined })
              }
            />
          </div>
          <Segmented
            value={showAll ? 'all' : 'active'}
            options={[
              { value: 'active', label: 'Đang hiệu lực' },
              { value: 'all', label: 'Tất cả' },
            ]}
            onChange={(v) => set({ active: v === 'all' ? 'all' : undefined })}
          />
        </div>
        <Button danger onClick={() => setRestrictOpen(true)}>
          Hạn chế một người
        </Button>
      </div>
      <div className="custom-table">
        <Table<FeedRestrictionRow>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: 'Không có ai đang bị hạn chế.' }}
          scroll={items.length ? { x: 960 } : undefined}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
      <RestrictModal open={restrictOpen} defaultScope="ALL" onClose={() => setRestrictOpen(false)} />
    </div>
  );
};

// ------------------------------------------------------------------ bị nhiều người chặn (D37)

const BlockedView = () => {
  const { refreshKey, goTab } = useFeed();
  const { get, set } = useUrlFilters();
  const page = Number(get('page')) || 1;
  const days = get('days') === '7' ? 7 : 30;
  const min = Number(get('min')) || 3;
  const [restrictUser, setRestrictUser] = useState<{ userId: number; name: string } | null>(null);
  const { items, total, loading } = useFeedList<FeedBlockedRow>(
    API.feedAdminBlocksSummary,
    { days, min, page, limit: PAGE_SIZE },
    { refreshKey }
  );

  const columns: ColumnsType<FeedBlockedRow> = [
    { title: 'Người', key: 'user', width: 192, render: (_, r) => <PersonCell person={r.user} /> },
    { title: 'Số người chặn', key: 'count', width: 120, render: (_, r) => <strong>{r.blockerCount}</strong> },
    {
      title: 'Lần chặn gần nhất',
      key: 'last',
      width: 144,
      render: (_, r) => <span style={{ fontSize: 13 }}>{fmtDateTime(r.lastBlockedAt)}</span>,
    },
    {
      title: 'Báo cáo đang mở',
      key: 'reports',
      width: 120,
      render: (_, r) =>
        r.openReportCount > 0 ? (
          <span style={{ color: colors.dangerInk, fontWeight: 600 }}>{r.openReportCount}</span>
        ) : (
          '0'
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button
            size="small"
            onClick={() => goTab('posts', { authorId: String(r.user.userId), authorName: r.user.name })}
          >
            Xem bài
          </Button>
          <Button size="small" danger onClick={() => setRestrictUser({ userId: r.user.userId, name: r.user.name })}>
            Hạn chế
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Segmented
          value={String(days)}
          options={[
            { value: '30', label: '30 ngày' },
            { value: '7', label: '7 ngày' },
          ]}
          onChange={(v) => set({ days: v === '7' ? '7' : undefined })}
        />
        <Space>
          <span>Ít nhất</span>
          <InputNumber min={1} max={100} value={min} onChange={(v) => set({ min: v && v !== 3 ? v : undefined })} />
          <span>người chặn</span>
        </Space>
      </div>
      <div className={styles.hint} style={{ marginBottom: 8 }}>
        Người bị nhiều người khác chặn trong khoảng thời gian đã chọn. Không hiện ai đã chặn (riêng tư của người chặn).
      </div>
      <div className="custom-table">
        <Table<FeedBlockedRow>
          rowKey={(r) => String(r.user?.userId)}
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: 'Chưa có ai bị nhiều người chặn.' }}
          scroll={items.length ? { x: 736 } : undefined}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
      <RestrictModal
        open={!!restrictUser}
        presetUser={restrictUser}
        defaultScope="ALL"
        onClose={() => setRestrictUser(null)}
      />
    </div>
  );
};

// ------------------------------------------------------------------ tab

/** Tab "Báo cáo" (03 §8): hàng chờ · đã xử lý · đã bỏ qua · người bị hạn chế · bị nhiều người chặn. */
const ReportsTab = () => {
  const { summary } = useFeed();
  const { get, clear } = useUrlFilters();
  const view = get('view') || 'OPEN';

  const changeView = (v: string) => clear(['tab', 'post'], v !== 'OPEN' ? { view: v } : undefined);

  return (
    <div>
      <div style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Segmented
          value={view}
          onChange={(v) => changeView(String(v))}
          options={[
            { value: 'OPEN', label: 'Chờ xử lý' },
            { value: 'RESOLVED', label: 'Đã xử lý' },
            { value: 'DISMISSED', label: 'Đã bỏ qua' },
            { value: 'restrictions', label: 'Người bị hạn chế' },
            {
              value: 'blocked',
              label: summary?.frequentlyBlocked
                ? `Bị nhiều người chặn (${summary.frequentlyBlocked})`
                : 'Bị nhiều người chặn',
            },
          ]}
        />
      </div>
      {view === 'restrictions' ? (
        <RestrictionsView />
      ) : view === 'blocked' ? (
        <BlockedView />
      ) : (
        <QueueView
          key={view}
          status={view === 'RESOLVED' ? 'RESOLVED' : view === 'DISMISSED' ? 'DISMISSED' : 'OPEN'}
        />
      )}
    </div>
  );
};

export default ReportsTab;
